import { connectedComponents } from "./connectedComponents";
import { aspectRatio, fillRatio } from "./geometry";
import { close } from "./morphology";
import { otsuThreshold } from "./otsu";
import { threshold } from "./threshold";
import type { BoundingBox, Component, GrayImage } from "./types";

export interface TileDetectionOptions {
  /** Minimum tile area as a fraction of the whole image. */
  minAreaFraction?: number;
  /** Maximum tile area as a fraction of the whole image. */
  maxAreaFraction?: number;
  /** Accepted bounding-box aspect-ratio range (long/short side). */
  minAspect?: number;
  maxAspect?: number;
  /** Minimum fraction of the bounding box the tile pixels must fill. */
  minFill?: number;
  /** Smallest accepted tile side length in pixels. */
  minSide?: number;
  /**
   * Radius (px) of the morphological closing applied before labelling. This
   * bridges the dark dividing line and fills pip holes so each tile is a single
   * connected blob. Set to 0 to disable.
   */
  closeRadius?: number;
}

export interface DetectedTile {
  bbox: BoundingBox;
  orientation: "horizontal" | "vertical";
}

const DEFAULTS: Required<TileDetectionOptions> = {
  minAreaFraction: 0.002,
  maxAreaFraction: 0.95,
  // Standard dominoes are 2:1; allow slack for perspective and merged borders.
  minAspect: 1.5,
  maxAspect: 3.5,
  minFill: 0.6,
  minSide: 12,
  // Small enough that adjacent tiles in a real photo stay separate blobs, but
  // still bridges the thin dividing line into a single tile.
  closeRadius: 2,
};

/**
 * Detect domino tiles in a grayscale image. Assumes dominoes are lighter than
 * the background (e.g. cream/white tiles on a darker, contrasting surface).
 *
 * The image is thresholded with Otsu's method to isolate the bright tiles, then
 * connected components are filtered by size, aspect ratio and fill so that only
 * domino-shaped rectangles remain. Results are returned in reading order
 * (down each column, columns left to right — see {@link readingOrder}).
 */
export function detectTiles(
  image: GrayImage,
  options: TileDetectionOptions = {},
): DetectedTile[] {
  const opts = { ...DEFAULTS, ...options };
  const t = otsuThreshold(image);
  const binary = threshold(image, { threshold: t, invert: false });
  const closed = close(binary, opts.closeRadius);
  const { components } = connectedComponents(closed, { connectivity: 8 });

  const totalPixels = image.width * image.height;
  const tiles = components
    .filter((c) => isTileShaped(c, totalPixels, opts))
    .map<DetectedTile>((c) => ({
      bbox: c.bbox,
      orientation: c.bbox.width >= c.bbox.height ? "horizontal" : "vertical",
    }));

  return readingOrder(tiles);
}

/**
 * Put detected tiles into reading order: down each column, columns left to
 * right. Groups laid out on a table are read one vertical column at a time, so
 * tiles are clustered into columns by horizontal position and each column is
 * then ordered top-to-bottom. A single row collapses to left-to-right and a
 * single column to top-to-bottom, the natural orders for those layouts.
 */
function readingOrder(tiles: DetectedTile[]): DetectedTile[] {
  if (tiles.length <= 1) return tiles;

  const centreX = (t: DetectedTile) => t.bbox.x + t.bbox.width / 2;
  const centreY = (t: DetectedTile) => t.bbox.y + t.bbox.height / 2;

  // Walk left to right, starting a new column whenever the horizontal gap to the
  // previous tile exceeds half a tile width (i.e. they no longer overlap).
  const byX = [...tiles].sort((a, b) => centreX(a) - centreX(b));
  const columns: DetectedTile[][] = [[byX[0]]];
  for (let i = 1; i < byX.length; i++) {
    const prev = byX[i - 1];
    const curr = byX[i];
    const gap = centreX(curr) - centreX(prev);
    const threshold = Math.min(prev.bbox.width, curr.bbox.width) / 2;
    if (gap > threshold) columns.push([]);
    columns[columns.length - 1].push(curr);
  }

  return columns.flatMap((column) =>
    column.sort((a, b) => centreY(a) - centreY(b)),
  );
}

function isTileShaped(
  c: Component,
  totalPixels: number,
  opts: Required<TileDetectionOptions>,
): boolean {
  const fraction = c.area / totalPixels;
  if (fraction < opts.minAreaFraction || fraction > opts.maxAreaFraction) {
    return false;
  }
  if (c.bbox.width < opts.minSide || c.bbox.height < opts.minSide) {
    return false;
  }
  const ar = aspectRatio(c.bbox);
  if (ar < opts.minAspect || ar > opts.maxAspect) {
    return false;
  }
  if (fillRatio(c.area, c.bbox) < opts.minFill) {
    return false;
  }
  return true;
}
